import loading from 'assets/pictures/loading.gif'
import { push } from '@lagunovsky/redux-react-router'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { orderBy, sortBy } from 'lodash'
import DtoSimpleStation from 'home/dto/DtoSimpleStation'
import Card from '../../components/card/Card'
import Table from '../../components/datatable/Table'
import { getDate } from '../../utils/DateUtil'
import { OldCampaignPortletPanel } from '../../campaign/components/CampaignPortletPanel'
import MonitoredMaterielPortletPanel from '../../materiel/components/dashboard/MonitoredMaterielPortletPanel'
import ProgressCard from '../../components/card/ProgressCard'
import { getUserBookmarksByStationType } from '../../utils/UserUtil'
import { componentHasHabilitations } from '../../utils/HabilitationUtil'
import {
    H_MAT_MODULE,
    H_PLUVIO_MODULE,
} from '../../account/constants/AccessRulesConstants'
import HabilitationRequired from '../../utils/components/HabilitationRequired'
import { STATION_TYPE_CONSTANT, STATION_TYPE_NAME } from 'station/constants/StationConstants'
import UserThunk from 'administration/components/user/actions/UserThunk'
import useStateProgress from 'utils/customHook/useStateProgress'
import PluviometryThunk from 'pluviometry/actions/PluviometryThunk'
import useAccountSetting from '../../utils/customHook/useAccountSetting'
import StationAction from '../../station/actions/StationAction'
import MapSituationPie from '../../station/components/mapSituation/MapSituationPie'
import { Grid, Grid2 } from '@mui/material'
import { NewTable } from '../../components/datatable/NewTable'

const panelTable = 'table'
const panelGraph = 'graph'
const pluvioStationType = 2

const PluviometryCard = () => {
    const {
        userBookmarks,
        pluviometers,
        monitoredMateriels,
        citiesIndex,
        userBookmarksStatus,
        pluviometersStatus,
        monitoredStations,
    } = useSelector(store => ({
        userBookmarks: store.UserReducer.userBookmarks,
        pluviometers: store.PluviometryReducer.pluviometers,
        monitoredMateriels: store.MaterielReducer.monitoredMateriels,
        citiesIndex: store.CityReducer.citiesIndex,
        userBookmarksStatus: store.DataManagerReducer.user.userBookmarksStatus,
        pluviometersStatus: store.DataManagerReducer.pluviometry.pluviometersStatus,
        monitoredStations: store.EventsReducer.monitoredStations,
    }), shallowEqual)

    const { progress: progressBar, isLoaded: dataLoaded } = useStateProgress([userBookmarksStatus, pluviometersStatus])

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(UserThunk.fetchBookmarks())
        dispatch(PluviometryThunk.fetchPluviometers())
    }, [])

    const [panelType, setPanelType] = useState(panelTable)
    const mapSituationWidgetObj = useAccountSetting('MAP_SITUATION_DASHBOARD_WIDGET', setting => JSON.parse(setting ?? '{}'))
    const [mapSituationStats, setMapSituationStats] = useState([])

    const bookmarks = useMemo(() => getUserBookmarksByStationType(userBookmarks, STATION_TYPE_NAME.pluviometry), [userBookmarks])

    useEffect(() => {
        const pluvioMapIds = Object.keys(mapSituationWidgetObj).filter(key => key.startsWith(STATION_TYPE_NAME.pluviometry) && !!mapSituationWidgetObj[key]).map(k => parseInt(k.replace('pluviometry-', '')))
        if (pluvioMapIds.length) {
            Promise.all(pluvioMapIds.map(id => StationAction.promiseMapSituationStats(STATION_TYPE_NAME.pluviometry, id))).then(jsonTab => setMapSituationStats(jsonTab.filter(t => t.length)))
        }
    }, [mapSituationWidgetObj])

    const stationsForTable = useMemo(() => bookmarks.reduce((acc, code) => {
        const bookmarksStations = bookmarks.map(code => pluviometers.find(s => s.code === code)).filter(p => !!p).map(s => ({ ...s, isBookmark: true }))
        const monitored = monitoredStations.filter(s => s.type === STATION_TYPE_CONSTANT.PLUVIOMETRY && pluviometers.some(s2 => s.id === s2.id)).map(s => ({ ...s, isMonitored: true }))
        const reduced = [...bookmarksStations, ...monitored].reduce((acc, s) => {
            const found = acc.find(s2 => s.id === s2.id)
            if (found) {
                return [...acc.filter(s2 => s2.id !== s.id), { ...s, ...found }]
            }
            return [...acc, s]
        }, [])
        return orderBy(reduced, [s => s.isMonitored && s.isBookmark ? 2 : (s.isMonitored ? 1 : 0), 'name'], ['desc', 'asc'])
    }, []), [pluviometers, bookmarks, monitoredStations])

    const data = useMemo(() => ({
        stations: pluviometers,
        bookmarks,
        icon: 'insert_chart',
        campaignPanel: null,
        campaigns: [],
        stationType: pluvioStationType,
        habilitation: componentHasHabilitations(H_PLUVIO_MODULE),
        monitoredMateriels: monitoredMateriels.filter(m => m.typeSite === pluvioStationType),
    }), [bookmarks, monitoredMateriels, pluviometers])

    const bookmarksStationPanel = useMemo(() => {
        if (!stationsForTable.length) {
            return null
        }
        const tableData = stationsForTable.map(station => {
            const cityFound = citiesIndex[station.townCode]
            const result = new DtoSimpleStation(station)
            return {
                ...result,
                city: cityFound ? `${cityFound.name} [${cityFound.code}]` : station.townCode,
                comment: station.eventComment,
                nullValue: {
                    value: '',
                    align: 'center',
                    leftIcon: station.isMonitored && station.isBookmark ? {
                        icon: 'notificationsNone',
                        color: 'red',
                        tooltip: i18n.oversight,
                    } : undefined,
                    rightIcon: {
                        icon: station.isBookmark ? 'star' : 'notificationsNone',
                        color: station.isBookmark ? 'orange' : 'red',
                        tooltip: station.isBookmark ? i18n.bookmark : i18n.oversight,
                    },
                },
            }
        })
        if (panelType === panelTable) {
            const mapSituationPies = mapSituationStats.map(tab => {
                return (
                    <Grid2 key={tab} size={4}><MapSituationPie stats={tab} st={STATION_TYPE_NAME.pluviometry} /></Grid2>
                )
            })
            const mapSituationCard = mapSituationPies.length ? (
                <Grid2 container justifyContent='center' alignItems='center' spacing={3}>
                    {mapSituationPies}
                </Grid2>
            ) : undefined
            return (
                <>
                    {mapSituationCard}
                    <NewTable
                        headers={['nullValue', 'code', 'city', 'name', 'comment']}
                        rows={tableData}
                        onClickRow={s => dispatch(push(`/station/pluviometry/${s.id}`))}
                    />
                </>
            )
        }
        return (
            <Card
                title={(
                    <a>
                        <i className='waves-effect material-icons'>grade</i>
                        {i18n.myFavourites} ({stationsForTable.length} {i18n.elements})
                    </a>
                )}
                actions={[{
                    iconName: 'list',
                    onClick: () => setPanelType(panelTable),
                }]}
            >
                {null}
            </Card>
        )
    }, [citiesIndex, stationsForTable, panelType])

    const bookmarksPanel = useMemo(() => {
        if (data?.bookmarks?.length > 0 && data?.stations?.length > 0) {
            return (
                <div className='row no-margin'>
                    <div className='section no-padding'>
                        <div className='row no-margin'>
                            {bookmarksStationPanel}
                        </div>
                    </div>
                </div>
            )
        }
        return null
    }, [bookmarksStationPanel, data?.bookmarks?.length, data?.stations?.length])

    const campaignsPanel = useMemo(() => {
        if (data.campaigns && data.campaigns.length > 0 && data.campaignHeader) {
            return (
                <div className='row no-margin'>
                    <div className='section no-padding'>
                        <div className='row no-margin'>
                            <OldCampaignPortletPanel
                                noHeader={true}
                                campaigns={data.campaigns}
                                stationType={STATION_TYPE_NAME.pluviometry}
                                tableHeader={data.campaignHeader}
                            />
                        </div>
                    </div>
                </div>
            )
        }
        return null
    }, [data.campaignHeader, data.campaigns])

    if (dataLoaded) {
        if (!(data.habilitation && ((data.bookmarks.length > 0 && data.stations.length > 0) || data.campaigns.length > 0 || data.monitoredMateriels.length > 0 || data?.exploitations?.length > 0)) || !(bookmarksPanel || campaignsPanel)) {
            return null
        }
        return (
            <div className='row no-margin padding-top-1'>
                <Card
                    title={(
                        <a className='waves-effect' onClick={() => dispatch(push(`/${data.redirect || STATION_TYPE_NAME.pluviometry}`))}>
                            <i className='material-icons '>{data.icon}</i>
                            {i18n[STATION_TYPE_NAME.pluviometry]}
                        </a>
                    )}
                >
                    <div className='padding-1 padding-bottom-0'>
                        {bookmarksPanel}
                        {campaignsPanel}
                        <HabilitationRequired habilitation={H_MAT_MODULE}>
                            <MonitoredMaterielPortletPanel noHeader={true} stationType={data.stationType} />
                        </HabilitationRequired>
                    </div>
                </Card>
            </div>
        )
    }
    return (
        <div className='padding-top-1'>
            <Card>
                <div className='row no-margin center-align padding-top-5'>
                    <img src={loading} />
                    <ProgressCard withoutCard message={i18n.pluviometryCardInProgress} progress={progressBar} />
                </div>
            </Card>
        </div>
    )
}

export default PluviometryCard